import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getUserStores } from '@/lib/supabase/stores';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Store as StoreIcon, Package, Users, MapPin, ExternalLink, Settings, PlusCircle, ShoppingBag } from '@/components/icons';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { StoreItem } from '@/lib/supabase/stores';

type Props = {
  onSelect?: (s: StoreItem) => void;
  refreshKey?: number;
  onSelectionChange?: (selected: StoreItem[]) => void;
  showActions?: boolean;
  onPublishSelected?: (stores: StoreItem[]) => void;
  onCreateClick?: () => void;
  showCreateButton?: boolean;
};

export default function StoreList({ onSelect, refreshKey, onSelectionChange, showActions, onPublishSelected, onCreateClick, showCreateButton = true }: Props) {
  const [stores, setStores] = useState<StoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserStores();
      setStores(data || []);
    } catch (e: any) {
      console.error('StoreList load error', e);
      setError(e?.message || 'Error loading stores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (typeof refreshKey !== 'undefined') load(); }, [refreshKey]);

  const location = useLocation();
  const isMyStoresView = !!location?.pathname && (location.pathname.startsWith('/tiendas/mis-tiendas') || location.pathname.startsWith('/mis-tiendas'));

  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1,2,3].map(i => (
        <Card key={i} className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex gap-4">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
              </div>
              <Skeleton className="h-9 w-full" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (error) return (
    <div className="p-4 md:p-6">
      <Card className="border-error bg-error-muted">
        <CardContent className="pt-6">
          <p className="text-red-800">Error al cargar tiendas: {error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (!stores || stores.length === 0) return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <Card className="border-dashed">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <StoreIcon className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-2">No tienes tiendas aún</h3>
            <p className="text-muted-foreground mb-6 text-sm">
              Crea tu primera tienda para empezar a vender tus productos
            </p>
            {showCreateButton && onCreateClick && (
              <Button 
                onClick={onCreateClick}
                size="lg"
                className="w-full max-w-xs"
              >
                <PlusCircle className="h-5 w-5 mr-2" />
                Crear mi primera tienda
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((store: StoreItem) => {
          const storeInitials = store.name ? store.name.substring(0, 2).toUpperCase() : 'ST';
          return (
            <Card
              key={store.id}
              className="group overflow-hidden hover:shadow-lg hover:border-primary/50 transition-all duration-200 cursor-pointer"
              onClick={() => onSelect && onSelect(store)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <Avatar className="h-12 w-12 border-2 border-muted">
                    <AvatarImage src={store.avatar_url || ''} alt={store.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {storeInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate mb-1">
                      {store.name || 'Sin nombre'}
                    </CardTitle>
                    <div className="flex items-center gap-1.5">
                      {store.is_public ? (
                        <Badge variant="default" className="text-xs px-1.5 py-0.5">
                          Pública
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                          Privada
                        </Badge>
                      )}
                    </div>
                    {store.slug && (
                      <Link
                        to={`/${store.slug}`}
                        className="mt-1 block text-xs text-muted-foreground hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        minibid.org/{store.slug}
                      </Link>
                    )}
                  </div>
                </div>
                {store.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                    {store.description}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground" title="Productos activos">
                    <Package className="h-4 w-4" />
                    <span>{store.active_product_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground" title="Miembros del equipo">
                    <Users className="h-4 w-4" />
                    <span>{store.member_count || 1}</span>
                  </div>
                  {typeof store.pending_order_count !== 'undefined' && store.pending_order_count > 0 && (
                    <div className="flex items-center gap-1.5 text-primary font-medium" title="Pedidos pendientes">
                      <ShoppingBag className="h-4 w-4" />
                      <span>{store.pending_order_count}</span>
                    </div>
                  )}
                </div>

                {/* Location */}
                {(store.location_display_name) && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-1">
                      {store.location_display_name || store.address}
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Link to={`/${store.slug || store.id}/tablero`} className="flex-1">
                    <Button 
                      variant="default" 
                      className="w-full group-hover:bg-primary/90"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Settings className="h-3.5 w-3.5 mr-1.5" />
                      Administrar
                    </Button>
                  </Link>
                  {store.slug && (
                    <Link to={`/${store.slug}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
